import { defineComponent, ref, watch, onMounted, onUnmounted, Transition } from 'vue'

export const HDropdown = defineComponent({
  props: {
    open: { type: Boolean },
    disabled: { type: Boolean, default: false },
    position: {
      type: String as () =>
        | 'topLeft'
        | 'top'
        | 'topRight'
        | 'bottomLeft'
        | 'bottom'
        | 'bottomRight',
      default: 'bottomRight',
    },
    overlay: { type: Function },
  },
  emits: ['update:open'],
  setup(props, { emit, slots }) {
    const open = ref(props.open)
    const disabled = ref(props.disabled)
    const rootRef = ref<HTMLElement | null>(null)

    if (!slots.default) {
      console.error('HDropdown: Default slot (trigger) is required')
    }
    if (!slots.overlay && !props.overlay) {
      console.error('HDropdown: "overlay" prop or slot is required')
    }
    const toggle = () => {
      if (!disabled.value) {
        open.value = !open.value
        emit('update:open', open.value)
      }
    }

    const close = () => {
      if (!disabled.value) {
        open.value = false
        emit('update:open', false)
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      const rootEl = rootRef.value
      const target = event.target as Node
      if (rootEl && !rootEl.contains(target) && open.value && !disabled.value) {
        close()
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })
    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    watch(
      () => props.open,
      (newValue) => {
        open.value = newValue
      },
      { immediate: true },
    )

    watch(
      () => props.disabled,
      (newValue) => {
        disabled.value = newValue
      },
      { immediate: true },
    )

    const positionCls = {
      topLeft: 'bottom-full left-0 mb-1',
      top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
      topRight: 'bottom-full right-0 mb-1',
      bottomLeft: 'top-full left-0 mt-1',
      bottom: 'top-full left-1/2 -translate-x-1/2 mt-1',
      bottomRight: 'top-full right-0 mt-1',
    }[props.position]

    return () => (
      <div class={['relative w-full h-full']} ref={rootRef}>
        {/* 默认插槽作为触发器 */}
        {slots.default
          ? slots.default({ open: open.value, disabled: disabled.value, toggle, close })
          : null}
        <Transition name="dropdown">
          {open.value && !disabled.value && (
            <div class={['absolute z-100 w-full min-w-[300px]', positionCls]}>
              {slots.overlay
                ? slots.overlay({ open: open.value, disabled: disabled.value, toggle, close })
                : props.overlay?.({ open: open.value, disabled: disabled.value, toggle, close })}
            </div>
          )}
        </Transition>
      </div>
    )
  },
})

export default HDropdown
